import React, { type JSX } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import Edit from '@mui/icons-material/Edit';
import { CustomTable, CustomTransComponent } from 'components';
import { useDebounce } from '@fiji/common/src/hooks';
import { Header, LoadConfigurationItem } from '@fiji/common/src/types';
import {
    useGetAllLoadsQuery,
    useUpdateLoadDetailsMutation,
    useGetDeviceByIdQuery,
} from '@fiji/common/src/features/deviceManagement/deviceApi';
import { BeginRampShedModal } from './BeginRampShedModal';
import { StripedProgressBar } from './StripedProgressBar';
import type { RampShedRecord, RampShedAction } from './types';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Close, Done, LooksOne } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNewCustomTranslations, useRBAC } from 'hooks';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { subscribeTopic, unsubscribeTopic } from '@fiji/common/src/mqtt_connection/mqttConnection';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';

const formatDelay = (seconds: number): string => {
    if (seconds <= 0) return '0 sec';
    return `${seconds} sec`;
};

const mapToRampShedRecord = (item: LoadConfigurationItem): RampShedRecord => ({
    id: item.loadId,
    loadId: item.loadId,
    loadNumber: Number(item.loadNumber),
    loadName: item.loadName,
    loadDescription: item.loadDescription,
    loadType: 'device',
    rampEnabled: item.rampShedDetails?.rampEnabled ?? false,
    rampDelay: item.rampShedDetails ? formatDelay(item.rampShedDetails.rampDelay) : undefined,
    shedEnabled: item.rampShedDetails?.shedEnabled ?? false,
    shedDelay: item.rampShedDetails ? formatDelay(item.rampShedDetails.shedDelay) : undefined,
    rampShedState: 'idle',
    isDisabled: !item.isLoadEnabled,
});

const LoadNumberCell = (record: RampShedRecord): JSX.Element => (
    <Box className="f-14 fw-600 bg-content text-white width-18 border-radius-4 flex-center-align">
        {record.loadNumber + 1}
    </Box>
);

const CapabilityCell = (enabled: boolean): JSX.Element =>
    enabled ? (
        <Done fontSize="small" sx={{ color: 'primary.main' }} />
    ) : (
        <Close fontSize="small" sx={{ color: 'text.disabled' }} />
    );

const DelayCell = (record: RampShedRecord, type: 'ramp' | 'shed'): JSX.Element => {
    const delay = type === 'ramp' ? record.rampDelay : record.shedDelay;
    const enabled = type === 'ramp' ? record.rampEnabled : record.shedEnabled;

    if (record.rampShedState === 'planned') {
        return (
            <Chip
                label={<CustomTransComponent translationKey={'RAMP_SHED:LABELS.PLANNED'} />}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, color: 'text.secondary', borderColor: 'divider' }}
            />
        );
    }
    if (!enabled || !delay) {
        return <></>;
    }
    return (
        <Typography variant="body2" color="text.primary">
            {delay}
        </Typography>
    );
};

const PullingDeviceState = (): JSX.Element => (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, px: 4 }}>
        <CircularProgress size={32} />
        <Typography variant="subtitle1" fontWeight={600} color="text.primary" textAlign="center">
            <CustomTransComponent translationKey={'RAMP_SHED:LABELS.PULLING_DEVICE_STATE'} />
        </Typography>
    </Stack>
);

const beginActionButtonSx = {
    height: 36,
    px: 2,
    borderColor: 'transparent',
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    textTransform: 'none',
    fontWeight: 600,
    fontFamily: 'Open Sans, sans-serif',
    fontSize: 14,
    lineHeight: '14px',
    flexShrink: 0,
    '&:hover': {
        borderColor: 'transparent',
        backgroundColor: 'primary.main',
    },
    '&.Mui-disabled': {
        borderColor: 'transparent',
        backgroundColor: '#E0EEF8',
        color: '#80BDE0',
        opacity: 1,
    },
} as const;

type RampShedTableProps = { isRampShedInProgress: boolean };

export const RampShedTable: React.FC<RampShedTableProps> = ({ isRampShedInProgress = false }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    useNewCustomTranslations('RAMP_SHED');
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canCreateLoadRampShed = hasPermission('load-ramp-shed_create-devices');
    const canEditLoadRampShed = hasPermission('load-ramp-shed_edit-devices');
    // const canViewLoadRampShed = hasPermission('load-ramp-shed_view-devices');
    // const canViewLoadRampShedExpired = hasPermission('load-ramp-shed_view-devices_hasExpired');
    // const canDeleteLoadRampShed = hasPermission('load-ramp-shed_delete-devices');
    const { realmName, deviceId } = useParams();
    const mqttConnectionStatus = useTypedSelector((state) => state['common']['mqttConnectionStatus']);
    const { data: profileData } = useGetUserProfileQuery({}) as ApiEndpointType<ApiResponseType<any>>;
    const { data: deviceData, isFetching: isDeviceFetching }: any = useGetDeviceByIdQuery(deviceId, {
        skip: !deviceId,
    });
    const connectionStatus = String(deviceData?.data?.connectionStatus ?? '').toUpperCase();
    const deviceStatus = String(deviceData?.data?.deviceStatus ?? '').toUpperCase();
    const isDeviceOfflineOrQuarantine =
        isDeviceFetching || !deviceData?.data || connectionStatus === 'OFFLINE' || deviceStatus === 'QUARANTINED';
    const realmId = profileData?.data?.currentRealmId;
    const orgId = deviceData?.data?.organizationId;
    const groupId = deviceData?.data?.groupId;

    const topic = React.useMemo(() => {
        if (realmId && orgId && groupId && deviceId) {
            const builtTopic = `BSSRM/COMMAND_TOPIC/${realmId}/${orgId}/${groupId}/${deviceId}`;
            return builtTopic;
        }
        return '';
    }, [realmId, orgId, groupId, deviceId]);

    const tableRef = React.useRef<any>(undefined);
    const actionRunIdRef = React.useRef<number>(0);
    const hasCompletedRef = React.useRef(false);
    const initiatedByMeRef = React.useRef(false);
    const mqttStartFallbackTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const operationTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const [searchInput, setSearchInput] = React.useState('');
    const [, debouncedSearch] = useDebounce(undefined, undefined, searchInput);

    const [payload, setPayload] = React.useState({
        page: 0,
        size: 10,
        searchKey: '',
        filters: {},
        sort: {},
    });

    const [rampShedModal, setRampShedModal] = React.useState<{ open: boolean; action: RampShedAction }>({
        open: false,
        action: 'ramp',
    });
    const [activeAction, setActiveAction] = React.useState<RampShedAction | null>(null);
    const activeActionRef = React.useRef<RampShedAction | null>(null);
    const setActiveActionSafe = (val: RampShedAction | null): void => {
        activeActionRef.current = val;
        setActiveAction(val);
    };

    const clearMqttStartFallbackTimer = (): void => {
        if (mqttStartFallbackTimerRef.current) {
            clearTimeout(mqttStartFallbackTimerRef.current);
            mqttStartFallbackTimerRef.current = null;
        }
    };

    const clearOperationTimer = (): void => {
        if (operationTimerRef.current) {
            clearTimeout(operationTimerRef.current);
            operationTimerRef.current = null;
        }
    };

    const clearAllRampShedTimers = (): void => {
        clearMqttStartFallbackTimer();
        clearOperationTimer();
    };

    const finalizeActionFromMqtt = (endedStatus: 'RAMP_ENDED' | 'SHED_ENDED'): void => {
        if (hasCompletedRef.current) {
            return;
        }

        hasCompletedRef.current = true;
        clearAllRampShedTimers();

        setActiveActionSafe(null);
        initiatedByMeRef.current = false;
        refetchLoadsRef.current();

        const completionKey =
            endedStatus === 'RAMP_ENDED' ? 'RAMP_SHED:LABELS.RAMP_COMPLETE' : 'RAMP_SHED:LABELS.SHED_COMPLETE';

        dispatch(
            setMessageContent({
                isOpen: true,
                message: t(completionKey),
                duration: 3000,
            })
        );
    };
    const [updateLoadDetails, { isLoading: isUpdatingLoadDetails }] = useUpdateLoadDetailsMutation();

    const {
        data: loadsData,
        isLoading,
        refetch: refetchLoads,
    }: any = useGetAllLoadsQuery({ deviceId, body: payload, isRampShedRequired: true }, { skip: !deviceId });

    const refetchLoadsRef = React.useRef(refetchLoads);
    React.useEffect(() => {
        refetchLoadsRef.current = refetchLoads;
    }, [refetchLoads]);

    React.useEffect(() => {
        if (mqttConnectionStatus !== true || !topic) {
            return;
        }

        subscribeTopic(topic, (rawMessage: any, messageTopic: string) => {
            if (messageTopic !== topic) {
                return;
            }

            let parsed: any;
            try {
                parsed = typeof rawMessage === 'string' ? JSON.parse(rawMessage) : rawMessage;
            } catch {
                return;
            }

            if (parsed?.type !== 'RAMP_SHED_STARTED') {
                return;
            }

            const mqttStatus = parsed?.status;
            const isRampShedLifecycleStatus =
                mqttStatus === 'RAMP_STARTED' ||
                mqttStatus === 'SHED_STARTED' ||
                mqttStatus === 'RAMP_ENDED' ||
                mqttStatus === 'SHED_ENDED';

            if (!isRampShedLifecycleStatus) {
                return;
            }

            switch (parsed?.status) {
                case 'RAMP_STARTED': {
                    clearMqttStartFallbackTimer();
                    hasCompletedRef.current = false;
                    setActiveActionSafe('ramp');

                    if (!initiatedByMeRef.current) {
                        dispatch(
                            setMessageContent({
                                isOpen: true,
                                message: t('RAMP_SHED:LABELS.RAMP_STARTED'),
                                duration: 3000,
                            })
                        );
                    }

                    const rampTimeout = Number(parsed?.rampShedTimeout);
                    if (Number.isFinite(rampTimeout) && rampTimeout > 0) {
                        const opDelay = rampTimeout - Date.now();
                        if (opDelay > 0) {
                            clearOperationTimer();
                            operationTimerRef.current = setTimeout(() => {
                                operationTimerRef.current = null;
                                if (hasCompletedRef.current) return;
                                if (activeActionRef.current === null) return;

                                hasCompletedRef.current = true;
                                clearAllRampShedTimers();
                                const timedOutAction = activeActionRef.current;
                                setActiveActionSafe(null);
                                initiatedByMeRef.current = false;
                                refetchLoadsRef.current();
                                dispatch(
                                    setMessageContent({
                                        isOpen: true,
                                        message: t(
                                            timedOutAction === 'ramp'
                                                ? 'RAMP_SHED:LABELS.RAMP_TIMEOUT'
                                                : 'RAMP_SHED:LABELS.SHED_TIMEOUT'
                                        ),
                                        duration: 3000,
                                    })
                                );
                            }, opDelay);
                        }
                    }
                    break;
                }
                case 'SHED_STARTED': {
                    clearMqttStartFallbackTimer();
                    hasCompletedRef.current = false;
                    setActiveActionSafe('shed');

                    if (!initiatedByMeRef.current) {
                        dispatch(
                            setMessageContent({
                                isOpen: true,
                                message: t('RAMP_SHED:LABELS.SHED_STARTED'),
                                duration: 3000,
                            })
                        );
                    }

                    const shedTimeout = Number(parsed?.rampShedTimeout);
                    if (Number.isFinite(shedTimeout) && shedTimeout > 0) {
                        const opDelay = shedTimeout - Date.now();
                        if (opDelay > 0) {
                            clearOperationTimer();
                            operationTimerRef.current = setTimeout(() => {
                                operationTimerRef.current = null;
                                if (hasCompletedRef.current) return;
                                if (activeActionRef.current === null) return;

                                hasCompletedRef.current = true;
                                clearAllRampShedTimers();
                                const timedOutAction = activeActionRef.current;
                                setActiveActionSafe(null);
                                initiatedByMeRef.current = false;
                                refetchLoadsRef.current();
                                dispatch(
                                    setMessageContent({
                                        isOpen: true,
                                        message: t(
                                            timedOutAction === 'ramp'
                                                ? 'RAMP_SHED:LABELS.RAMP_TIMEOUT'
                                                : 'RAMP_SHED:LABELS.SHED_TIMEOUT'
                                        ),
                                        duration: 3000,
                                    })
                                );
                            }, opDelay);
                        }
                    }
                    break;
                }
                case 'RAMP_ENDED':
                    finalizeActionFromMqtt('RAMP_ENDED');
                    break;
                case 'SHED_ENDED':
                    finalizeActionFromMqtt('SHED_ENDED');
                    break;
                default:
                    break;
            }
        });
        return () => {
            unsubscribeTopic(topic);
            clearAllRampShedTimers();
        };
    }, [mqttConnectionStatus, topic, dispatch, t]);

    React.useEffect(() => {
        if (debouncedSearch === undefined) return;
        setPayload((prev) => ({ ...prev, page: 0, searchKey: String(debouncedSearch) }));
    }, [debouncedSearch]);

    const records: RampShedRecord[] = React.useMemo(
        () => (loadsData?.data?.loadConfiguration ?? []).map(mapToRampShedRecord),
        [loadsData]
    );
    const totalRecords = loadsData?.data?.total ?? 0;

    const loading = isLoading;

    const handlePageChange = (newPage: number, newSize: number): void => {
        setPayload((prev) => ({ ...prev, page: newPage, size: newSize }));
    };

    const handleFilterChange = (filters: any, sortData: any): void => {
        setPayload((prev) => ({
            ...prev,
            page: 0,
            filters: filters ?? {},
            ...(sortData && Object.keys(sortData).length ? { sort: sortData } : {}),
        }));
    };

    const openRampShedModal = (action: RampShedAction): void => setRampShedModal({ open: true, action });

    const handleRampShedConfirm = (): void => {
        if (!deviceId) {
            return;
        }

        const selectedAction = rampShedModal.action;
        const triggerType: 'BEGIN_RAMP' | 'BEGIN_SHED' = selectedAction === 'ramp' ? 'BEGIN_RAMP' : 'BEGIN_SHED';

        const actionLabel = selectedAction === 'ramp' ? t('RAMP_SHED:LABELS.RAMP') : t('RAMP_SHED:LABELS.SHED');

        setRampShedModal({ open: false, action: 'ramp' });

        const requestPayload = {
            triggerRequest: {
                isTriggered: true,
                type: triggerType,
            },
        } as const;

        void updateLoadDetails({
            deviceId,
            body: requestPayload,
        }).then((res: any) => {
            if (res?.error) {
                setActiveActionSafe(null);
                dispatch(
                    setMessageContent({
                        isOpen: true,
                        message: t('RAMP_SHED:LABELS.ACTION_FAILED', {
                            replace: { actionLabel },
                        }),
                        duration: 3000,
                    })
                );
            } else {
                const actionRunId = actionRunIdRef.current + 1;
                actionRunIdRef.current = actionRunId;
                hasCompletedRef.current = false;
                initiatedByMeRef.current = true;
                setActiveActionSafe(selectedAction);

                dispatch(
                    setMessageContent({
                        isOpen: true,
                        message: t(
                            selectedAction === 'ramp'
                                ? 'RAMP_SHED:LABELS.RAMP_STARTED'
                                : 'RAMP_SHED:LABELS.SHED_STARTED'
                        ),
                        duration: 3000,
                    })
                );

                clearAllRampShedTimers();
                mqttStartFallbackTimerRef.current = setTimeout(() => {
                    mqttStartFallbackTimerRef.current = null;
                    if (hasCompletedRef.current) return;
                    if (actionRunIdRef.current !== actionRunId) return;

                    hasCompletedRef.current = true;
                    clearAllRampShedTimers();
                    setActiveActionSafe(null);
                    initiatedByMeRef.current = false;
                    refetchLoadsRef.current();
                    dispatch(
                        setMessageContent({
                            isOpen: true,
                            message: t(
                                selectedAction === 'ramp'
                                    ? 'RAMP_SHED:LABELS.RAMP_TIMEOUT'
                                    : 'RAMP_SHED:LABELS.SHED_TIMEOUT'
                            ),
                            duration: 3000,
                        })
                    );
                }, 30000);
            }
        });
    };

    const handleEditClick = (): void => {
        navigate(`/${realmName}/ramp-shed-edit/${deviceId ?? ''}/`, {
            state: { from: location.pathname + location.hash },
        });
    };

    const handleChipDelete = (key: string): void => {
        tableRef?.current?.resetFilters(key, true);
        setPayload((prev) => {
            const filtersClone: Record<string, any> = { ...(prev.filters as Record<string, any>) };
            delete filtersClone[key];

            return { ...prev, page: 0, filters: filtersClone };
        });
    };

    const getChipName = (key: string): JSX.Element | string => {
        switch (key) {
            case 'name':
            case 'loadName':
                return <CustomTransComponent translationKey={'COMMON:NAME'} />;
            case 'loadDescription':
                return <CustomTransComponent translationKey={'ALARM_DETAILS:DESCRIPTION_TITLE'} />;
            default:
                return key;
        }
    };

    const tableColumns: Header[] = [
        {
            header: (
                <>
                    <CustomTransComponent translationKey={'HEALTH_STATUS:LOAD'} /> #
                </>
            ) as any,
            width: '7%',
            accessor: 'loadNumber',
            cell: (r: RampShedRecord): JSX.Element => LoadNumberCell(r),
            isSortable: true,
        },
        {
            header: (<CustomTransComponent translationKey={'COMMON:NAME'} />) as any,
            width: '18%',
            accessor: 'loadName',
            isSortable: true,
            isFilterable: true,
        },
        {
            header: (<CustomTransComponent translationKey={'ALARM_DETAILS:DESCRIPTION_TITLE'} />) as any,
            width: '30%',
            accessor: 'loadDescription',
            isFilterable: true,
        },
        {
            header: (<CustomTransComponent translationKey={'RAMP_SHED:HEADERS.RAMP'} />) as any,
            width: '7%',
            accessor: 'rampEnabled',
            cell: (r: RampShedRecord): JSX.Element => CapabilityCell(r.rampEnabled),
            isSortable: true,
        },
        {
            header: (<CustomTransComponent translationKey={'RAMP_SHED:HEADERS.DELAY'} />) as any,
            width: '10%',
            accessor: 'rampDelay',
            cell: (r: RampShedRecord): JSX.Element => DelayCell(r, 'ramp'),
            isSortable: true,
        },
        {
            header: (<CustomTransComponent translationKey={'RAMP_SHED:HEADERS.SHED'} />) as any,
            width: '7%',
            accessor: 'shedEnabled',
            cell: (r: RampShedRecord): JSX.Element => CapabilityCell(r.shedEnabled),
            isSortable: true,
        },
        {
            header: (<CustomTransComponent translationKey={'RAMP_SHED:HEADERS.DELAY'} />) as any,
            width: '10%',
            accessor: 'shedDelay',
            cell: (r: RampShedRecord): JSX.Element => DelayCell(r, 'shed'),
            isSortable: true,
        },
    ];

    const activeFilters = React.useMemo(() => payload.filters ?? {}, [payload.filters]);

    const activeChipItems = React.useMemo(
        () =>
            Object.keys(activeFilters)
                .map((key: string) => ({
                    key,
                    label: getChipName(key),
                    onDelete: (): void => handleChipDelete(key),
                }))
                .reverse(),
        [activeFilters, t]
    );

    return (
        <>
            <Box p={3}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                >
                    <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                        <TextField
                            size="small"
                            placeholder={t('RAMP_SHED:PLACEHOLDERS.SEARCH')}
                            value={searchInput}
                            onChange={(e): void => setSearchInput(e.target.value)}
                            sx={{ width: 256, flexShrink: 0 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {activeAction !== null && (
                            <Box sx={{ flex: 1, alignSelf: 'center' }}>
                                <StripedProgressBar
                                    variant="indeterminate"
                                    height={36}
                                    label={`${
                                        activeAction === 'ramp'
                                            ? t('RAMP_SHED:LABELS.RAMP')
                                            : t('RAMP_SHED:LABELS.SHED')
                                    } ${t('RAMP_SHED:LABELS.IN_PROGRESS')}`}
                                />
                            </Box>
                        )}

                        {activeAction === null && <Box sx={{ flex: 1 }} />}
                        {activeChipItems.length > 0 && (
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                                {activeChipItems.map((chip) => (
                                    <Chip
                                        key={`ramp-shed-filter-${chip.key}`}
                                        label={chip.label}
                                        onDelete={chip.onDelete}
                                        size="small"
                                        sx={{ bgcolor: 'grey.100', flexShrink: 0 }}
                                    />
                                ))}
                            </Stack>
                        )}

                        {(canCreateLoadRampShed || canEditLoadRampShed) && (
                            <>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={(): void => openRampShedModal('ramp')}
                                    disabled={
                                        isUpdatingLoadDetails ||
                                        activeAction !== null ||
                                        isRampShedInProgress ||
                                        isDeviceOfflineOrQuarantine
                                    }
                                    sx={beginActionButtonSx}
                                >
                                    <CustomTransComponent translationKey={'RAMP_SHED:BUTTONS.BEGIN_RAMP'} />
                                </Button>

                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={(): void => openRampShedModal('shed')}
                                    disabled={
                                        isUpdatingLoadDetails ||
                                        activeAction !== null ||
                                        isRampShedInProgress ||
                                        isDeviceOfflineOrQuarantine
                                    }
                                    sx={beginActionButtonSx}
                                >
                                    <CustomTransComponent translationKey={'RAMP_SHED:BUTTONS.BEGIN_SHED'} />
                                </Button>

                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Edit sx={{ fontSize: '16px !important' }} />}
                                    onClick={handleEditClick}
                                    disabled={
                                        isUpdatingLoadDetails ||
                                        activeAction !== null ||
                                        isRampShedInProgress ||
                                        isDeviceOfflineOrQuarantine
                                    }
                                    sx={{
                                        height: 36,
                                        pl: 1.5,
                                        pr: 2,
                                        borderColor: 'divider',
                                        color: 'primary.main',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        flexShrink: 0,
                                        '&.Mui-disabled': {
                                            borderColor: 'divider',
                                            color: 'text.disabled',
                                        },
                                    }}
                                >
                                    <CustomTransComponent translationKey={'RAMP_SHED:BUTTONS.EDIT'} />
                                </Button>
                            </>
                        )}
                    </Stack>
                </Stack>
            </Box>
            <Box>
                <Paper className="box-shadow">
                    {records.length || Object.keys(payload?.filters ?? {}).length || payload?.searchKey?.length ? (
                        <CustomTable
                            ref={tableRef}
                            data={records}
                            headers={tableColumns}
                            keyToTraverse="id"
                            isLoading={loading}
                            total={totalRecords}
                            handlePageChange={handlePageChange}
                            handleFilterChange={handleFilterChange}
                            disableCheckbox={true}
                            controlledPageSize={payload.page}
                            isPagination={true}
                            disableRow={(record: RampShedRecord): boolean => record.isDisabled}
                            noDataFoundIcon={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LooksOne sx={{ fontSize: 48, color: 'text.disabled' }} />
                                </Box>
                            }
                            noDataFoundTitle={<CustomTransComponent translationKey={'WIDGETS:LOADS.NO_LOADS_FOUND'} />}
                            noDataFoundButtonText={<CustomTransComponent translationKey={'LOADS:ADD_LOADS'} />}
                        />
                    ) : (
                        <PullingDeviceState />
                    )}
                </Paper>
            </Box>
            <BeginRampShedModal
                open={rampShedModal.open}
                action={rampShedModal.action}
                selectedCount={0}
                onConfirm={handleRampShedConfirm}
                onCancel={(): void => setRampShedModal({ open: false, action: 'ramp' })}
            />
        </>
    );
};
